import { Link } from "react-router-dom";
import { ArrowRight, Cpu, FlaskConical, Eye, ShieldCheck, Landmark, KeyRound, UserCheck, Wallet } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "../components/Reveal";
import HeroVisual from "../components/HeroVisual";
import Marquee from "../components/Marquee";
import SectionHeading from "../components/SectionHeading";
import { OfferingCard } from "../components/Cards";
import PerformancePanel, { LabelLegend } from "../components/PerformancePanel";
import RiskNote from "../components/RiskNote";
import { CTABand } from "../components/PageParts";
import { OFFERINGS } from "../config/site";
import { scrollToSection } from "../lib/scroll";

const HERO_BG =
  "https://images.unsplash.com/photo-1760978631985-590e3b5f4057?crop=entropy&cs=srgb&fm=jpg&q=85";

const PILLARS = [
  {
    icon: Cpu,
    title: "Technology-driven trading",
    text: "Systematic, rules-based approaches built on quantitative research — not discretionary guesswork.",
  },
  {
    icon: FlaskConical,
    title: "Systematic strategy research",
    text: "Strategies are developed, stress-tested and reviewed under a documented research process.",
  },
  {
    icon: Eye,
    title: "Transparent operating models",
    text: "Every offering states its custody structure, mechanics and terms plainly — including when data is not yet available.",
  },
  {
    icon: ShieldCheck,
    title: "Reporting & verification",
    text: "Performance is labelled by category — backtested, simulated, live, audited, independently verified — and never mixed.",
  },
];

const CUSTODY_DISTINCTION = [
  {
    icon: KeyRound,
    title: "Non-custodial",
    offerings: "Master Trading · Strategy Trading",
    text: "Assets remain in your own exchange account, or trade through API keys on your account with trade permission only. We never take custody.",
  },
  {
    icon: Landmark,
    title: "Custodial",
    offerings: "Vault Trading · Vault Yield",
    text: "Assets are held under a custodial arrangement with the exchange, host platform or issuer. Counterparty and custody risk applies and is disclosed.",
  },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Algorithmic Trading & Digital Asset Vaults"
        path="/"
        description="Exchange-hosted master trading, API-connected strategies, custodial vault trading and independently verified yield opportunities from Kalki AI Fund."
      />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden min-h-screen flex items-center pt-28 pb-16 lg:pt-24" data-testid="home-hero">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-[0.16]" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-900/60 via-ink-900/80 to-ink-900" />
        </div>
        <div className="absolute inset-0 bg-grid-fade opacity-70" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-8 items-center">
            <div>
              <Reveal delay={0.05}>
                <span className="eyebrow mb-8">Digital Asset Algorithmics · kalkiai.fund</span>
              </Reveal>

              <MaskedLines
                lines={[
                  "Intelligent Trading.",
                  "Structured Capital.",
                  { text: "Transparent Performance.", className: "text-gradient-gold" },
                ]}
                delay={0.2}
                className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[4.4rem] font-bold tracking-tighter leading-[1.05]"
              />

              <Reveal delay={0.7}>
                <p className="mt-8 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl">
                  Explore a new approach to algorithmic trading and digital asset opportunities
                  through Kalki AI Fund. Discover exchange-hosted master trading, API-connected
                  strategies, custodial vault trading, and independently verified yield
                  opportunities.
                </p>
              </Reveal>

              <Reveal delay={0.85}>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link to="/vault-trading" data-testid="hero-cta-vaults" className="btn-gold">
                    Explore Our Vaults
                    <ArrowRight size={16} />
                  </Link>
                  <button
                    data-testid="hero-cta-models"
                    onClick={() => scrollToSection("offerings")}
                    className="btn-outline"
                  >
                    Discover Our Models
                  </button>
                </div>
              </Reveal>

              <Reveal delay={1}>
                <div className="mt-12 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">
                  <span className="flex items-center gap-2"><span className="live-dot" />Exchange-hosted</span>
                  <span className="flex items-center gap-2"><span className="live-dot" />API-connected</span>
                  <span className="flex items-center gap-2"><span className="live-dot" />Independently verified</span>
                </div>
              </Reveal>
            </div>

            <div className="relative">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* ============ FOUR OFFERINGS ============ */}
      <section id="offerings" className="relative py-24 lg:py-32" data-testid="home-offerings">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Four Core Offerings"
            title={<>Four structures. One transparent standard.</>}
            intro="Each model is presented with its custody arrangement, operating mechanics and risks stated plainly — so you always know exactly how your assets are held."
          />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-2">
            {OFFERINGS.map((o) => (
              <StaggerItem key={o.id} className="h-full">
                <OfferingCard offering={o} />
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============ PERFORMANCE & TRANSPARENCY ============ */}
      <section id="performance" className="relative py-24 lg:py-32 border-t border-gold-400/10" data-testid="home-performance">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Performance & Transparency"
            title={<>Verified data only.<br />Placeholders when it isn't.</>}
            intro="Where verified data is available we publish it in full, with its category label and source. Where it is not yet available, we show an informational placeholder — never an invented number."
          />
          <div className="mt-14">
            <PerformancePanel />
            <LabelLegend />
          </div>
        </div>
      </section>

      {/* ============ ABOUT PREVIEW ============ */}
      <section id="about" className="relative py-24 lg:py-32 border-t border-gold-400/10" data-testid="home-about">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionHeading
                eyebrow="About Kalki AI Fund"
                title={<>An institutional approach to digital asset trading.</>}
                intro="Kalki AI Fund exists to make systematic digital asset strategies understandable: how they operate, who holds the assets, and what the risks are."
              />
              <Stagger className="mt-10 grid sm:grid-cols-2 gap-5">
                {PILLARS.map((p) => (
                  <StaggerItem key={p.title}>
                    <div className="glass rounded-2xl p-5 h-full hover:border-gold-400/30 transition-colors duration-500">
                      <p.icon size={20} className="text-gold-400 mb-4" />
                      <h3 className="text-sm font-semibold text-ink-50 mb-2">{p.title}</h3>
                      <p className="text-xs text-zinc-500 leading-relaxed">{p.text}</p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
              <Reveal delay={0.2}>
                <Link
                  to="/about"
                  data-testid="about-preview-link"
                  className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-gold-300 hover:text-gold-200 transition-colors group"
                >
                  Read the full approach
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
              </Reveal>
            </div>

            <Reveal delay={0.2} y={36} className="relative">
              <div className="frame-gold overflow-hidden rounded-sm" data-testid="home-about-image">
                <img
                  src="https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Institutional technology infrastructure in a dark data centre"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-300">
                    Systematic · Documented · Reviewed
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* custody distinction */}
          <Stagger className="mt-16 grid md:grid-cols-2 gap-6">
            {CUSTODY_DISTINCTION.map((c) => (
              <StaggerItem key={c.title}>
                <div className="glass-gold rounded-3xl p-8 h-full" data-testid={`custody-${c.title.toLowerCase()}`}>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold-400/30 bg-gold-400/[0.07]">
                      <c.icon size={18} className="text-gold-300" />
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-bold text-ink-50">{c.title}</h3>
                      <p className="font-mono text-[10px] tracking-[0.14em] text-gold-400/80 uppercase mt-0.5">
                        {c.offerings}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ============ RISK NOTE ============ */}
      <section className="py-16 border-t border-gold-400/10">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <RiskNote
            lines={[
              "Digital asset trading involves substantial risk of loss. Master Trading and Strategy Trading are non-custodial; Vault Trading and Vault Yield are custodial and carry counterparty risk. Past performance — backtested, simulated, live or verified — never guarantees future results.",
              "Read the full Risk Disclosure before engaging with any offering.",
            ]}
          />
          <Reveal delay={0.1}>
            <Link
              to="/risk-disclosure"
              data-testid="home-risk-link"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold-300 hover:text-gold-200 transition-colors group"
            >
              Full Risk Disclosure
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABand
        testid="home-cta"
        title="Start with the model that fits your capital structure."
        subtitle="Compare custody arrangements, review the disclosures, and speak with the team when you're ready."
        primary={{ label: "Contact Our Team", to: "/contact" }}
        secondary={{ label: "Explore Our Vaults", to: "/vault-trading" }}
      />
    </>
  );
}